import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axiosV2 from "@lhd/libs/axiosV2";
import type { WorkerLogtimeChangeHistory } from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type WorkerLogtimeCompanyId = number | string;

export type WorkerLogtimeSortDirection = "asc" | "desc";

export type WorkerLogtimeMonthlyV2Params = {
  company_id?: WorkerLogtimeCompanyId;
  year?: number;
  month?: number;
  worker_id?: number;
  page?: number;
  per_page?: number;
  sort?: Record<string, WorkerLogtimeSortDirection>;
  filters?: Record<string, string[]>;
};

export type WorkerLogtimeTimeValue = {
  time: string | null;
  display_time: string | null;
  at: string | null;
  next_day: boolean;
};

export type WorkerLogtimePunchBreak = {
  start: WorkerLogtimeTimeValue | null;
  end: WorkerLogtimeTimeValue | null;
};

export type WorkerLogtimeCorrectedValue = WorkerLogtimeTimeValue & {
  id: number;
  original_time: string | null;
  original_display_time: string | null;
  modified: boolean;
  missing: boolean;
  edit_count: number;
  updated_at: string;
};

export type WorkerLogtimeCorrectedBreak = {
  start: WorkerLogtimeCorrectedValue | null;
  end: WorkerLogtimeCorrectedValue | null;
};

export type WorkerLogtimeMonthlyV2Record = {
  date: string;
  company_name: string;
  worker_id: number;
  worker_name: string;
  punch: {
    check_in: WorkerLogtimeTimeValue | null;
    check_out: WorkerLogtimeTimeValue | null;
    breaks: WorkerLogtimePunchBreak[];
  };
  corrected: {
    check_in: WorkerLogtimeCorrectedValue | null;
    check_out: WorkerLogtimeCorrectedValue | null;
    breaks: WorkerLogtimeCorrectedBreak[];
  };
  note_id: number | null;
  note_updated_at: string | null;
  note: string | null;
  working_time: string | null;
  break_time: string | null;
  logtime_status: string;
  updater_name: string | null;
  updated_at: string | null;
  check_out_missing: boolean;
  break_end_missing: boolean;
  can_view: boolean;
  can_edit: boolean;
  can_create: boolean;
  can_delete: boolean;
  [key: string]: unknown;
};

export type WorkerLogtimeMonthlyV2Response = {
  year: number;
  month: number;
  company_id: number;
  company_name: string;
  meta: {
    max_break_pairs: number;
  };
  summary: {
    work_days: number;
    working_hours: string;
    break_hours: string;
  };
  pagination: {
    current_page: number;
    per_page: number;
    total_records: number;
    total_pages: number;
  };
  data: WorkerLogtimeMonthlyV2Record[];
};

export type WorkerLogtimeDayChangeTimeResponse = {
  day_change_time: string;
  can_edit_day_change_time: boolean;
};

export type WorkerLogtimeDayChangeTimePayload = {
  company_id?: WorkerLogtimeCompanyId;
  day_change_time: string;
};

export type WorkerLogtimeDayChangeTimeUpdateResponse = {
  day_change_time: string;
  can_edit_day_change_time?: boolean;
};

export type WorkerLogtimeBulkSaveValue = {
  id?: number;
  logged_at?: string;
  updated_at?: string;
  _destroy?: boolean;
};

export type WorkerLogtimeBulkSaveBreak = {
  start?: WorkerLogtimeBulkSaveValue | null;
  end?: WorkerLogtimeBulkSaveValue | null;
};

export type WorkerLogtimeBulkSaveRecord = {
  worker_id: number;
  date: string;
  check_in?: WorkerLogtimeBulkSaveValue | null;
  check_out?: WorkerLogtimeBulkSaveValue | null;
  breaks?: WorkerLogtimeBulkSaveBreak[];
  note_id?: number;
  note_updated_at?: string;
  note?: string;
};

export type WorkerLogtimeBulkSavePayload = {
  company_id?: WorkerLogtimeCompanyId;
  records: WorkerLogtimeBulkSaveRecord[];
};

export type WorkerLogtimeBulkSaveErrorCode =
  | "version_required"
  | "conflict"
  | "record_not_found"
  | "forbidden"
  | "worker_not_found"
  | "invalid";

export type WorkerLogtimeBulkSaveError = {
  field: string;
  code: WorkerLogtimeBulkSaveErrorCode;
  message: string;
  current_updated_at?: string;
};

export type WorkerLogtimeBulkSaveResult = {
  worker_id: number;
  date: string;
  status: "ok" | "error";
  record?: WorkerLogtimeMonthlyV2Record;
  errors?: WorkerLogtimeBulkSaveError[];
};

export type WorkerLogtimeBulkSaveResponse = {
  saved_count: number;
  failed_count: number;
  results: WorkerLogtimeBulkSaveResult[];
};

export type WorkerLogtimeFilterOption =
  | string
  | {
      value: string;
      label: string;
    };

export type WorkerLogtimeFilterOptionsV2Params = {
  company_id?: WorkerLogtimeCompanyId;
  year: number;
  month: number;
  active_filters?: Record<string, string[]>;
  fields?: string[];
};

export type WorkerLogtimeFilterOptionsV2Response = Record<
  string,
  WorkerLogtimeFilterOption[]
>;

export type WorkerLogtimeHistoriesV2Params = {
  company_id?: WorkerLogtimeCompanyId;
  worker_id: number;
  date: string;
  worker_logtime_id?: number;
};

export type WorkerLogtimeHistoryV2 = WorkerLogtimeChangeHistory;

export type WorkerLogtimeHistoriesV2Response = {
  histories: WorkerLogtimeHistoryV2[];
  total: number;
};

const WORKER_LOGTIMES_V2_QUERY_KEY = [
  QUERY_NAME.WORKER_LOGTIMES,
  "v2",
] as const;
const WORKER_LOGTIMES_BULK_LIMIT = 200;

const serializeWorkerLogtimeParams = (params: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, String(item)));
    } else if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const buildWorkerLogtimeMonthlyParams = (
  params: WorkerLogtimeMonthlyV2Params,
  companyId: WorkerLogtimeCompanyId,
) => {
  const { sort, filters, ...rest } = params;
  const requestParams: Record<string, unknown> = {
    ...rest,
    company_id: companyId,
  };

  Object.entries(sort || {}).forEach(([field, direction]) => {
    requestParams[`sort[${field}]`] = direction;
  });
  Object.entries(filters || {}).forEach(([field, values]) => {
    requestParams[`filters[${field}][]`] = values;
  });

  return requestParams;
};

const buildWorkerLogtimeFilterOptionsParams = (
  params: WorkerLogtimeFilterOptionsV2Params,
  companyId: WorkerLogtimeCompanyId,
) => {
  const { active_filters: activeFilters, fields, ...rest } = params;
  const requestParams: Record<string, unknown> = {
    ...rest,
    company_id: companyId,
  };

  if (fields?.length) {
    requestParams["fields[]"] = fields;
  }
  Object.entries(activeFilters || {}).forEach(([field, values]) => {
    requestParams[`active_filters[${field}][]`] = values;
  });

  return requestParams;
};

/** GET /api/v2/worker_logtimes/monthly */
export const useFetchWorkerLogtimesMonthlyV2 = (
  params: WorkerLogtimeMonthlyV2Params = {},
  enabled = true,
) => {
  const defaultCompanyId = getCompanyId();
  const companyId = params.company_id ?? defaultCompanyId;

  return useQuery<WorkerLogtimeMonthlyV2Response>(
    [...WORKER_LOGTIMES_V2_QUERY_KEY, "monthly", companyId, params],
    () =>
      axiosV2<WorkerLogtimeMonthlyV2Response>({
        method: "get",
        url: `${SERVICES.WORKER_LOGTIMES}/monthly`,
        params: buildWorkerLogtimeMonthlyParams(params, companyId),
        paramsSerializer: { serialize: serializeWorkerLogtimeParams },
      }).then((response) => response.data),
    {
      enabled: enabled && !!companyId,
      keepPreviousData: true,
      staleTime: 300,
      cacheTime: 300,
    },
  );
};

/** GET /api/v2/worker_logtimes/day_change_time */
export const useFetchWorkerLogtimesDayChangeTimeV2 = (
  companyIdParam?: WorkerLogtimeCompanyId,
  enabled = true,
) => {
  const defaultCompanyId = getCompanyId();
  const companyId = companyIdParam ?? defaultCompanyId;

  return useQuery<WorkerLogtimeDayChangeTimeResponse>(
    [...WORKER_LOGTIMES_V2_QUERY_KEY, "day_change_time", companyId],
    () =>
      axiosV2<WorkerLogtimeDayChangeTimeResponse>({
        method: "get",
        url: `${SERVICES.WORKER_LOGTIMES}/day_change_time`,
        params: { company_id: companyId },
      }).then((response) => response.data),
    {
      enabled: enabled && !!companyId,
      staleTime: 300,
      cacheTime: 300,
    },
  );
};

/** PUT /api/v2/worker_logtimes/day_change_time */
export const useUpdateWorkerLogtimesDayChangeTimeV2 = () => {
  const defaultCompanyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    async (payload: WorkerLogtimeDayChangeTimePayload) => {
      const companyId = payload.company_id ?? defaultCompanyId;
      if (!companyId) {
        throw new Error("company_id is required");
      }

      const response = await axiosV2<WorkerLogtimeDayChangeTimeUpdateResponse>({
        method: "put",
        url: `${SERVICES.WORKER_LOGTIMES}/day_change_time`,
        data: {
          company_id: companyId,
          day_change_time: payload.day_change_time,
        },
      });

      return response.data;
    },
    {
      onSuccess(response, payload) {
        const companyId = payload.company_id ?? defaultCompanyId;
        queryClient.setQueryData<WorkerLogtimeDayChangeTimeResponse>(
          [...WORKER_LOGTIMES_V2_QUERY_KEY, "day_change_time", companyId],
          (current) => ({
            day_change_time: response.day_change_time,
            can_edit_day_change_time:
              response.can_edit_day_change_time ??
              current?.can_edit_day_change_time ??
              false,
          }),
        );
      },
    },
  );
};

export const bulkSaveWorkerLogtimesV2 = async (
  payload: Required<Pick<WorkerLogtimeBulkSavePayload, "company_id">> &
    Omit<WorkerLogtimeBulkSavePayload, "company_id">,
): Promise<WorkerLogtimeBulkSaveResponse> => {
  const batches: WorkerLogtimeBulkSaveRecord[][] = [];

  for (
    let index = 0;
    index < payload.records.length;
    index += WORKER_LOGTIMES_BULK_LIMIT
  ) {
    batches.push(
      payload.records.slice(index, index + WORKER_LOGTIMES_BULK_LIMIT),
    );
  }

  const combinedResponse: WorkerLogtimeBulkSaveResponse = {
    saved_count: 0,
    failed_count: 0,
    results: [],
  };

  for (const records of batches) {
    const response = await axiosV2<WorkerLogtimeBulkSaveResponse>({
      method: "post",
      url: `${SERVICES.WORKER_LOGTIMES}/bulk_save`,
      data: {
        company_id: payload.company_id,
        records,
      },
    });
    combinedResponse.saved_count += response.data.saved_count;
    combinedResponse.failed_count += response.data.failed_count;
    combinedResponse.results.push(...response.data.results);
  }

  return combinedResponse;
};

/** POST /api/v2/worker_logtimes/bulk_save */
export const useSaveWorkerLogtimesBulkV2 = () => {
  const defaultCompanyId = getCompanyId();
  const queryClient = useQueryClient();

  return useMutation(
    (payload: WorkerLogtimeBulkSavePayload) => {
      const companyId = payload.company_id ?? defaultCompanyId;
      if (!companyId) {
        return Promise.reject(new Error("company_id is required"));
      }
      return bulkSaveWorkerLogtimesV2({
        ...payload,
        company_id: companyId,
      });
    },
    {
      onSettled() {
        queryClient.invalidateQueries([QUERY_NAME.WORKER_LOGTIMES]);
      },
    },
  );
};

/** GET /api/v2/worker_logtimes/filter_options */
export const useFetchWorkerLogtimesFilterOptionsV2 = (
  params: WorkerLogtimeFilterOptionsV2Params | null,
  enabled = true,
) => {
  const defaultCompanyId = getCompanyId();
  const companyId = params?.company_id ?? defaultCompanyId;

  return useQuery<WorkerLogtimeFilterOptionsV2Response>(
    [...WORKER_LOGTIMES_V2_QUERY_KEY, "filter_options", companyId, params],
    async () => {
      if (!params) {
        throw new Error("filter options params are required");
      }
      const response = await axiosV2<WorkerLogtimeFilterOptionsV2Response>({
        method: "get",
        url: `${SERVICES.WORKER_LOGTIMES}/filter_options`,
        params: buildWorkerLogtimeFilterOptionsParams(params, companyId),
        paramsSerializer: { serialize: serializeWorkerLogtimeParams },
      });
      return response.data;
    },
    {
      enabled: enabled && !!params && !!companyId,
      keepPreviousData: true,
      staleTime: 300,
      cacheTime: 300,
    },
  );
};

/** GET /api/v2/worker_logtimes/histories */
export const useFetchWorkerLogtimeHistoriesV2 = (
  params: WorkerLogtimeHistoriesV2Params | null,
  enabled = true,
) => {
  const defaultCompanyId = getCompanyId();
  const companyId = params?.company_id ?? defaultCompanyId;

  return useQuery<WorkerLogtimeHistoriesV2Response>(
    [...WORKER_LOGTIMES_V2_QUERY_KEY, "histories", companyId, params],
    async () => {
      if (!params) {
        throw new Error("history params are required");
      }
      const response = await axiosV2<WorkerLogtimeHistoriesV2Response>({
        method: "get",
        url: `${SERVICES.WORKER_LOGTIMES}/histories`,
        params: {
          company_id: companyId,
          worker_id: params.worker_id,
          date: params.date,
          worker_logtime_id: params.worker_logtime_id,
        },
      });
      return response.data;
    },
    {
      enabled:
        enabled &&
        !!params &&
        !!companyId &&
        !!params.worker_id &&
        !!params.date,
      staleTime: 300,
      cacheTime: 300,
    },
  );
};
